import React from 'react';
import * as d3 from 'd3';
import { navigate } from 'gatsby';

import { css } from '@emotion/react';
import { useUpdateInteractionMutation } from '$/GraphQL/schema/schema';
import { useWebinarPlayerContext, WebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { BoxedText } from '../BoxedText';
import { ArrowLeftIcon, ArrowRightIcon, PauseIcon, Spinner } from '$/images/Icons';
import { MarkerData } from '../Markers/types';

import { RoomType } from '$/GraphQL/schema/types';
import formatTimeInWebinarSecs from '$/utils/formatTimeInWebinarSecs';
import stripTypenames from '$/utils/stripTypenames';

import { useFormContext } from 'react-hook-form';
import { ZoomScaleProviderContext } from '../Timeline/ZoomScaleProviderContext';
import { readableColorOnBackground } from '$/utils/colors';
import { logger } from '$/utils/logger';
import { minRangeAppearEnd, timelineComponentMargin } from './variables';

interface TimeLabelConfig {
  text: string;
  x: number;
}

interface RangeControlState {
  inEdit: boolean;
  isDraggable: boolean;
  hasPermission: boolean;
  showPermissionModal: () => void;
  draggingId: string | null;
  setDraggingId: (id: string | null) => void;
  hoveringId: string | null;
  setHoveringId: (id: string | null) => void;
  onInteractionIconClick?: (id: string) => void;
}

interface RangeControlDisplayState {
  ref: (dom: SVGElement | null) => void;
  timeLabel: TimeLabelConfig;
  hoveringText: string;
  updating: boolean;
}

const useMarkerHandleControl = (
  marker: MarkerData,
  name: 'playbackPosition' | 'endsAt',
  state: RangeControlState
): RangeControlDisplayState => {
  const { zoomScale, setZoomFocusAt, zoom, x } = React.useContext(ZoomScaleProviderContext);
  const { rooms, playbackPosition, ewebinar, setPlaybackPosition, setIsPlaying, currentRoom } =
    useWebinarPlayerContext();
  const [updateInteraction, updateInteractionResult] = useUpdateInteractionMutation();
  const [hoveringText, setHoveringText] = React.useState('');

  const width = zoom?.translateExtent()[1][0] ?? 1;
  const zoomConvertTime = (position: number): number => {
    const p = zoomScale ? (zoomScale(position) as number) : x!(position) ?? 1;
    return Math.max(0, width ? Math.min(p, width) : p);
  };

  const isAppearAt = name === 'playbackPosition';

  const appearAt = marker.showAt;
  const appearAtX = zoomConvertTime(appearAt);
  const endsAt = marker.endsAt || 0;
  const endsAtX = zoomConvertTime(endsAt);

  const formHandlers = useFormContext();
  const [timeLabel, setTimeLabel] = React.useState<TimeLabelConfig>({
    text: formatTimeInWebinarSecs(isAppearAt ? appearAt : endsAt) || '0',
    x: isAppearAt ? appearAtX : endsAtX,
  });

  const ref = React.useCallback(
    (dom: Element | null) => {
      if (!dom) return;
      const root = d3.select(dom);

      let posTime = isAppearAt ? appearAt : endsAt;
      let posX = isAppearAt ? appearAtX : endsAtX;

      let timePosition = formatTimeInWebinarSecs(posTime) || '0';
      const markerCount = marker.count ?? 0;

      let skewTime = 0.0;
      let skewX = 0;
      let hasTraveled = false;

      if (!state.draggingId) {
        root.style('cursor', state.inEdit ? 'grab' : 'pointer');
      }

      function onClick(event: React.MouseEvent): void {
        if (state.inEdit) {
          return;
        }

        event.stopPropagation();
        if (!setZoomFocusAt) return;
        if (markerCount) {
          setZoomFocusAt(marker);
        } else {
          if (!state.hasPermission) {
            state.showPermissionModal();
            return;
          }
          if (state.onInteractionIconClick) {
            state.onInteractionIconClick(marker.interaction.id!);
          } else {
            // Edit interaction
            let url = '';

            const matches = /^(.*\/interactions)/.exec(location.pathname);
            if (matches && matches.length >= 2) {
              url = `${matches[1]}/${marker.interaction.id}`;
            }

            !markerCount && currentRoom && navigate(url);

            // Pause player when editing an interaction at interaction's playback position
            logger.debug('PLAY', 'On Edit Interaction ISPAYING FALSE');
            setIsPlaying(false);
            setPlaybackPosition(marker.playbackPosition);
          }
        }
      }

      root.on('click', onClick);
      root.on('mouseover', () => {
        if (state.draggingId) return;
        setHoveringText(
          markerCount > 1 ? `${markerCount} interactions` : marker.interaction.title()
        );
        state.setHoveringId(marker.interaction.id ?? '');
      });
      root.on('mouseout', () => {
        state.setHoveringId('');
      });

      if (appearAt <= endsAt - minRangeAppearEnd) {
        const width = zoom!.translateExtent()[1][0];
        posX = Math.min(Math.max(posX, 0), width);
        posTime = zoomScale?.invert(posX);
      }

      timePosition = formatTimeInWebinarSecs(posTime) || '0';
      setTimeLabel({
        text: hoveringText || state.inEdit ? timePosition : '',
        x: posX,
      });

      state.isDraggable &&
        root.call(
          d3
            .drag()
            .clickDistance(5)
            .subject(() => zoomScale!(playbackPosition))
            .on('start', (event) => {
              if (markerCount > 0) {
                return;
              }

              root.style('cursor', 'grabbing');
              hasTraveled = false;
              const xOffset = event.x;
              skewX = posX - xOffset;
              skewTime = posTime - zoomScale.invert(xOffset);
              state.setDraggingId(marker.interaction.id!);
            })

            .on('drag', (event) => {
              let xOffset = event.x;
              const distance = Math.abs(xOffset - posX + skewX);
              if (distance >= 10) {
                hasTraveled = true;
              }
              if (!hasTraveled) {
                // Require a snap out of existing position so a quick click/unclick
                // doesn't cause an interaction to move a second or two and get saved
                return;
              }

              let position = zoomScale.invert(xOffset) + skewTime;
              // Assure playbackPosition is valid range
              const room = rooms!.getRoomAt(position);

              if (
                room.type !== RoomType.Presentation &&
                marker.interaction.onlyExistsInPresentationRoom()
              ) {
                const presentationRoom = rooms.getRoom(RoomType.Presentation);

                position = Math.min(
                  presentationRoom.appearAt + presentationRoom.duration,
                  Math.max(presentationRoom.appearAt, position)
                );

                xOffset = zoomScale(position) as number;
              } else {
                xOffset = xOffset + skewX;
              }

              // Assure appearAt is not after endAt
              if (isAppearAt && marker.interaction.hasEndsAt() && position > endsAt) {
                position = endsAt;
                xOffset = zoomScale(position) as number;
              }
              if (!isAppearAt && position < appearAt) {
                position = appearAt;
                xOffset = zoomScale(position) as number;
              }

              const timeFormatted = formatTimeInWebinarSecs(position) || '';
              setTimeLabel({
                text: timeFormatted,
                x: xOffset,
              });
            })

            .on('end', async (event) => {
              root.style('cursor', state.inEdit ? 'grab' : 'pointer');

              let xOffset = event.x;
              if (!hasTraveled) {
                // Haven't snapped out of the beginning position
                // Ignore drag
                state.setDraggingId(null);
                return;
              }

              let playbackPosition = Math.floor(zoomScale?.invert(xOffset));
              if (playbackPosition === undefined) {
                state.setDraggingId(null);
                return;
              }
              playbackPosition = playbackPosition + skewTime;

              // Assure playbackPosition is valid range
              playbackPosition = Math.min(
                Math.max(playbackPosition, rooms.domain[0]),
                rooms.domain[1]
              );

              const [, roomType] = rooms.appearAtDuoFromPosition(playbackPosition);

              const presentationRoom = rooms.getRoom(RoomType.Presentation);
              if (
                roomType !== RoomType.Presentation &&
                marker.interaction.onlyExistsInPresentationRoom()
              ) {
                // Restrict playbackPosition to Presentation room
                playbackPosition = isAppearAt
                  ? Math.min(
                      presentationRoom.appearAt,
                      Math.max(presentationRoom.appearAt, playbackPosition)
                    )
                  : Math.min(playbackPosition, ewebinar.duration);
              }

              xOffset = xOffset + skewX;

              // Assure appearAt is not after endAt
              if (isAppearAt && marker.interaction.hasEndsAt() && playbackPosition > endsAt) {
                playbackPosition = endsAt;
                xOffset = zoomScale(playbackPosition) as number;
              }
              if (!isAppearAt && playbackPosition < appearAt) {
                playbackPosition = appearAt;
                xOffset = zoomScale(playbackPosition) as number;
              }
              const timeFormatted = formatTimeInWebinarSecs(playbackPosition) || '';
              setTimeLabel({
                text: timeFormatted,
                x: xOffset,
              });

              try {
                if (state.inEdit) {
                  formHandlers.setValue(`details.${name}`, Math.floor(playbackPosition), {
                    shouldDirty: true,
                  });
                  formHandlers.trigger([`details.${name}`]);
                } else {
                  const [inRoomPlaybackPosition, roomType] =
                    rooms.appearAtDuoFromPosition(playbackPosition);

                  const i = marker.interaction;

                  // This leads to failures with new fields which is better than no error but forgotten fields
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const origFragment = { ...(stripTypenames(i.fragment) as any) };
                  const publishAsTemplate = Boolean(origFragment.templateId);
                  delete origFragment.v;
                  delete origFragment.createdAt;
                  delete origFragment.updatedAt;
                  delete origFragment.isNewTemplate;
                  delete origFragment.valueType;
                  delete origFragment.interactionShortId;
                  delete origFragment.templateId;

                  await updateInteraction({
                    variables: {
                      data: {
                        ...origFragment,
                        publishAsTemplate,

                        room: name === 'playbackPosition' ? roomType : i.room.type,
                        appearAt:
                          name === 'playbackPosition' ? inRoomPlaybackPosition : i.appearAt(),

                        details: {
                          title: i.title(),
                          ...stripTypenames(i.details),
                          endsAt: name === 'endsAt' ? playbackPosition : i.details?.endsAt,
                        },
                      },
                    },
                  });
                }
              } catch (e) {
                console.error(e);
              } finally {
                state.setDraggingId(null);
              }
            })
        );
    },
    [
      appearAt,
      appearAtX,
      currentRoom,
      endsAt,
      endsAtX,
      ewebinar.duration,
      formHandlers,
      hoveringText,
      isAppearAt,
      marker,
      name,
      playbackPosition,
      rooms,
      setIsPlaying,
      setPlaybackPosition,
      setZoomFocusAt,
      state,
      updateInteraction,
      zoom,
      zoomScale,
    ]
  );

  return {
    ref,
    timeLabel,
    hoveringText: hoveringText,
    updating: updateInteractionResult.called && updateInteractionResult.loading,
  };
};

const RangeEndMarker: React.FC<{
  marker: MarkerData;
  state: RangeControlState;
  display: RangeControlDisplayState;
  endOfRange?: boolean;
}> = ({ marker, state, display, endOfRange }) => {
  const Icon = marker.interaction.icon();
  const { colors, timelineComponents } = React.useContext(WebinarPlayerContext);
  const { zoom } = React.useContext(ZoomScaleProviderContext);
  const markerCount = marker.count ?? 0;

  const interaction = marker.interaction;
  const isRange = interaction.hasEndsAt();
  const component = interaction.timelineComponent();
  const h = (timelineComponents[component]?.height ?? 0) - timelineComponentMargin;
  const r = h / 2;

  const ArrowIcon = endOfRange ? ArrowRightIcon : ArrowLeftIcon;

  const coloredBg =
    state.inEdit || endOfRange || marker.interaction.timelineComponent() === 'anchor';
  const bgColor =
    marker.interaction.timelineComponent() !== 'anchor'
      ? interaction.bgMarkerColor(coloredBg)
      : '#fff';

  const fgColor =
    marker.interaction.timelineComponent() !== 'anchor' &&
    (interaction.fragment.templateId || interaction.fragment.isFavorite)
      ? '#fff'
      : coloredBg
        ? readableColorOnBackground(bgColor)
        : marker.interaction.color();

  const endOfTimeline = zoom!.translateExtent()[1][0] - 12;
  const xTimeLabel = display.timeLabel.x;
  const xPosition =
    xTimeLabel > 12 ? (xTimeLabel > endOfTimeline ? endOfTimeline : xTimeLabel) : 12;

  const MarkerHandle: React.FC = React.useMemo(
    () => (): React.ReactElement => {
      switch (marker.interaction.timelineComponent()) {
        case 'anchor':
          {
            const h = 15;
            const cx = display.timeLabel.x;
            //const cyTop = (timelineComponents.title?.offset ?? 0) + h / 2;
            const cyBottom = (timelineComponents.time?.offset ?? 0) - h / 2;

            return (
              <g
                css={css`
                  color: ${bgColor};
                `}
              >
                {/*
                <line
                  x1={cx}
                  y1={timelineComponents.title?.offset ?? 0}
                  x2={cx}
                  y2={cyBottom}
                  className='anchor'
                  stroke={bgColor}
                />
                 Rect to handle hover / click regardless of underlying icon shape*/}
                <rect
                  width={h * 1.2}
                  height={h * 1.2}
                  x={xPosition - (h * 1.2) / 2}
                  y={timelineComponents.time.offset - h * 1.2 - 2}
                  fill='#00000000'
                />
                {display.updating ? (
                  React.cloneElement(<Spinner spin />, {
                    preserveAspectRatio: 'xMidYMid meet',
                    alignmentBaseline: 'central',
                    height: h,
                    width: h,
                    x: display.timeLabel.x - h / 2,
                    y: timelineComponents.time.offset - h - 2,
                    className: 'icon',
                    fill: '#fff',
                    animation: 'icon-loading 2s linear infinite',
                  })
                ) : (
                  <>
                    <polygon
                      className='anchor handle'
                      points={`${xPosition - h / 2},${cyBottom + h / 2}, ${xPosition - 1},${
                        cyBottom - h / 2 + 1
                      },  ${xPosition + 1},${cyBottom - h / 2 + 1}, ${xPosition + h / 2},${
                        cyBottom + h / 2
                      }`}
                      stroke='#00000000'
                      fill={'#fff'}
                    />
                    <text
                      x={xPosition}
                      y={cyBottom + h / 8}
                      textAnchor={'middle'}
                      alignmentBaseline={'central'}
                      fill={fgColor}
                      css={css`
                        font-size: 10px;
                      `}
                    >
                      {markerCount
                        ? markerCount
                        : marker.interaction.details?.shortcut?.substring(0, 1)}
                    </text>
                  </>
                )}
                {marker.interaction.details?.anchor?.pause &&
                  React.cloneElement(
                    <PauseIcon
                      css={css`
                        color: #fff;
                      `}
                    />,
                    {
                      className: 'anchor',
                      preserveAspectRatio: 'xMidYMid meet',
                      alignmentBaseline: 'central',
                      height: timelineComponents.time.height - 2,
                      width: timelineComponents.time.height - 2,
                      y: timelineComponents.time.offset + 1,
                      x: cx - (timelineComponents.time.height - 2) / 2 + 0.5,
                    }
                  )}
              </g>
            );
          }
          break;

        default: {
          const cy =
            (timelineComponents[component]?.offset ?? 0) +
            (timelineComponents[component]?.height ?? 0) / 2;

          return (
            <g className='handle'>
              <circle
                fontSize='1.5em'
                cy={cy}
                cx={xPosition}
                strokeWidth={state.inEdit || (state.isDraggable && endOfRange) ? 0 : 0.5}
                stroke={fgColor}
                fill={bgColor}
                r={r}
              />

              {markerCount && !endOfRange ? (
                <text
                  y={cy}
                  x={xPosition}
                  fill={fgColor}
                  textAnchor='middle'
                  alignmentBaseline='central'
                >
                  {markerCount}
                </text>
              ) : display.updating ? (
                React.cloneElement(<Spinner spin />, {
                  preserveAspectRatio: 'xMidYMid meet',
                  alignmentBaseline: 'central',
                  height: r,
                  width: r,
                  x: display.timeLabel.x - r / 2,
                  y: cy - r / 2,
                  className: 'cursor icon',
                  style: { color: fgColor },
                })
              ) : (state.inEdit && isRange) || (state.isDraggable && endOfRange) ? (
                React.cloneElement(<ArrowIcon />, {
                  preserveAspectRatio: 'xMidYMid meet',
                  alignmentBaseline: 'central',
                  height: r,
                  width: r,
                  x: display.timeLabel.x - (endOfRange ? r / 4 : (2 * r) / 4),
                  y: cy - r / 2,
                  className: 'cursor icon',
                  style: { color: fgColor },
                })
              ) : state.inEdit || !endOfRange ? (
                Icon ? (
                  <Icon
                    preserveAspectRatio='xMidYMid meet'
                    alignmentBaseline='central'
                    height={r * 1.25}
                    width={r * 1.25}
                    x={display.timeLabel.x - (r * 1.25) / 2}
                    y={cy - (r * 1.25) / 2}
                    className='cursor icon'
                    style={{ color: fgColor }}
                  />
                ) : (
                  <svg />
                )
              ) : null}
            </g>
          );
          break;
        }
      }
    },
    [
      ArrowIcon,
      Icon,
      bgColor,
      component,
      display.timeLabel.x,
      display.updating,
      endOfRange,
      fgColor,
      isRange,
      marker.interaction,
      markerCount,
      r,
      state.inEdit,
      state.isDraggable,
      timelineComponents,
      xPosition,
    ]
  );

  return (
    <g ref={display.ref} className='marker'>
      <MarkerHandle />
      <BoxedText
        bgColor={colors.controlBg}
        textColor={colors.text}
        alignmentBaseline='middle'
        textAnchor={endOfRange === undefined ? 'middle' : endOfRange ? 'end' : 'start'}
        x={display.timeLabel.x + 1}
        y={timelineComponents.time.offset + timelineComponents.time.height / 2}
        className={`${state.draggingId !== marker.interaction.id && 'hidden'}`}
      >
        {display.timeLabel.text}
      </BoxedText>
      <BoxedText
        id='hover'
        x={display.timeLabel.x}
        y={timelineComponents.time.offset + timelineComponents.time.height / 2}
        minHeight={timelineComponents.time.height}
        maxHeight={timelineComponents.time.height}
        bgColor={colors.controlBg}
        textColor='rgba(255, 255, 255, 0.8)'
        textAnchor='middle'
        alignmentBaseline='middle'
        className={`${
          !state.draggingId && !state.inEdit && state.hoveringId === marker.interaction.id
            ? ''
            : 'hidden'
        }`}
      >
        {`${display.hoveringText}${isRange ? (endOfRange ? ' end' : ' start') : ''}`}
      </BoxedText>
    </g>
  );
};

const Marker: React.FC<{
  marker: MarkerData;
  isDraggable: boolean;
  hasPermission: boolean;
  showPermissionModal: () => void;
  draggingId: string | null;
  setDraggingId: (dragging: string | null) => void;
  hoveringId: string | null;
  setHoveringId: (id: string | null) => void;
  onInteractionIconClick?: (id: string) => void;
}> = ({
  marker,
  isDraggable,
  draggingId,
  setDraggingId,
  hoveringId,
  setHoveringId,
  onInteractionIconClick,
  hasPermission,
  showPermissionModal,
}) => {
  const { timelineComponents, interactionInEdit } = React.useContext(WebinarPlayerContext);

  const interaction = marker.interaction;
  const isRange = interaction.hasEndsAt();
  const component = interaction.timelineComponent();

  const state: RangeControlState = React.useMemo(() => {
    return {
      inEdit: Boolean(interactionInEdit),
      isDraggable,
      hasPermission,
      showPermissionModal,
      draggingId,
      setDraggingId,
      hoveringId,
      setHoveringId,
      onInteractionIconClick,
    };
  }, [
    draggingId,
    hasPermission,
    hoveringId,
    interactionInEdit,
    isDraggable,
    onInteractionIconClick,
    setDraggingId,
    setHoveringId,
    showPermissionModal,
  ]);

  const left = useMarkerHandleControl(marker, 'playbackPosition', state);
  const right = useMarkerHandleControl(marker, 'endsAt', state);

  const reactWidth = right.timeLabel.x - left.timeLabel.x;

  return (
    <svg
      key={interaction.id}
      cursor='pointer'
      className='cursorAppearEnd overflow-visible'
      css={css`
        .marker {
          .handle {
            overflow: visible;
            transition: all 0.3s;
            transform-origin: center;
            transform-box: fill-box;
            path {
              pointer-events: none;
            }
          }
          ${!state.inEdit
            ? `
            &:hover .handle {
              transform: scale(1.5);
            }
          `
            : ''}
        }
      `}
    >
      <svg className='overflow-visible'>
        {!isRange ? null : (
          <rect
            onClick={(e): void => {
              if (!state.inEdit && onInteractionIconClick) {
                e.preventDefault();
                onInteractionIconClick(interaction.id!);
              }
            }}
            height={(timelineComponents[component]?.height ?? 0) - timelineComponentMargin}
            y={(timelineComponents[component]?.offset ?? 0) + timelineComponentMargin / 2}
            x={left.timeLabel.x}
            width={`${Math.max(0, reactWidth)}`}
            fill={interaction.color()}
          />
        )}

        {isRange && (
          /* Render end marker first so it is hidden by start marker on scroll past end */
          <RangeEndMarker marker={marker} state={state} display={right} endOfRange={true} />
        )}

        <RangeEndMarker
          marker={marker}
          state={state}
          display={left}
          endOfRange={isRange ? false : undefined}
        />
      </svg>
    </svg>
  );
};

export default Marker;
